/**
 * FotografiasTab - Componente principal da aba Fotografias
 *
 * Epic 2 - Story 2.2: Refatorado para usar API real
 * Quando jobId é fornecido, busca dados da API /api/jobs/{jobId}/evidencias/
 * Quando não há jobId, usa dados mockados para compatibilidade
 */
import { useState } from 'react';
import { Camera, Image as ImageIcon, FileText, MapPin, Loader2 } from 'lucide-react';
import { useFotografias, MockFoto } from '../../hooks/useFotografias';
import { useEvidencias, Evidencia } from '../../hooks/useEvidencias';
import { FotografiasFilters } from './FotografiasFilters';
import { FotografiasGrid } from './FotografiasGrid';
import { FotografiasPorClienteView } from './FotografiasPorClienteView';
import { FotoLightbox } from './FotoLightbox';

interface FotografiasTabProps {
  // ID do job para buscar evidências da API real
  jobId?: number;
  // Filtro global de cliente (vem do header da página ContagemCompletaPage)
  globalClienteFilter?: (string | number)[];
  // Lista de clientes do job (para resolver nomes na view por cliente)
  jobClientes?: Array<{ id: number; nome_fantasia?: string; razao_social?: string }>;
}

/**
 * Adapta uma Evidencia da API para o formato MockFoto usado pelos componentes
 */
function evidenciaToMockFoto(evidencia: Evidencia): MockFoto {
  // Determina tipo de arquivo baseado no tipo de evidência
  const isPdf = evidencia.tipo === 'DOCUMENTO';

  return {
    id: evidencia.id,
    fileName: evidencia.arquivo_nome || `Evidencia_${evidencia.id}`,
    type: isPdf ? 'pdf' : 'image',
    classificacao: evidencia.classificacao_nome || evidencia.tipo_display || evidencia.tipo,
    classificacaoAbrev: evidencia.classificacao_abrev || evidencia.tipo.replace('FOTO_', 'Ev_').replace('DOCUMENTO', 'Doc'),
    cliente: evidencia.captured_by_name || 'Desconhecido',
    clienteId: evidencia.cliente_id || 0,
    descricao: evidencia.descricao || undefined,
    dataUpload: evidencia.created_at,
    tamanho: evidencia.file_size_display || 'N/A',
    // Campos extras para GPS (Epic 2 - Story 2.4)
    hasGps: evidencia.has_gps,
    gpsDisplay: evidencia.gps_display,
    arquivoUrl: evidencia.arquivo_url,
  };
}

export function FotografiasTab({ jobId, globalClienteFilter, jobClientes }: FotografiasTabProps) {
  const [viewMode, setViewMode] = useState<'grid' | 'por_cliente'>('por_cliente');
  // Usa API real quando jobId é fornecido, senão usa mockup
  const useRealApi = jobId !== undefined && jobId > 0;

  // Hook da API real (apenas quando jobId existe)
  const evidenciasHook = useEvidencias({
    jobId: jobId || 0,
    globalClienteFilter,
    autoLoad: useRealApi,
  });

  // Hook de mockup (fallback quando não há jobId)
  const mockupHook = useFotografias(globalClienteFilter);

  // Converte evidências da API para formato MockFoto
  const fotosFromApi = useRealApi
    ? evidenciasHook.evidencias.map(evidenciaToMockFoto)
    : [];

  // Seleciona dados baseado na fonte (API ou mockup)
  const fotos = useRealApi ? fotosFromApi : mockupHook.fotos;
  const loading = useRealApi ? evidenciasHook.loading : false;
  const error = useRealApi ? evidenciasHook.error : null;

  // Contadores
  const counts = useRealApi ? evidenciasHook.counts : mockupHook.counts;

  // Lightbox state - usa hook apropriado
  const lightboxOpen = useRealApi ? evidenciasHook.lightboxOpen : mockupHook.lightboxOpen;
  const currentPhotoIndex = useRealApi ? evidenciasHook.currentPhotoIndex : mockupHook.currentPhotoIndex;
  const openLightbox = useRealApi ? evidenciasHook.openLightbox : mockupHook.openLightbox;
  const closeLightbox = useRealApi ? evidenciasHook.closeLightbox : mockupHook.closeLightbox;
  const goToPrevious = useRealApi ? evidenciasHook.goToPrevious : mockupHook.goToPrevious;
  const goToNext = useRealApi ? evidenciasHook.goToNext : mockupHook.goToNext;

  // Filtros - usa hook apropriado
  const selectedTipoGeral = useRealApi ? evidenciasHook.selectedTipo : mockupHook.selectedTipoGeral;
  const setSelectedTipoGeral = useRealApi ? evidenciasHook.setSelectedTipo : mockupHook.setSelectedTipoGeral;
  const searchTerm = useRealApi ? evidenciasHook.searchTerm : mockupHook.searchTerm;
  const setSearchTerm = useRealApi ? evidenciasHook.setSearchTerm : mockupHook.setSearchTerm;
  const hasActiveFilters = useRealApi ? evidenciasHook.hasActiveFilters : mockupHook.hasActiveFilters;
  const clearAllFilters = useRealApi ? evidenciasHook.clearAllFilters : mockupHook.clearAllFilters;

  // Opções de filtro - mockup tem mais opções, API tem opções simplificadas
  const tipoGeralOptions = useRealApi ? evidenciasHook.tipoOptions : mockupHook.tipoGeralOptions;
  const classificacaoOptions = mockupHook.classificacaoOptions; // Sempre do mockup (API não tem esse filtro)
  const tipoArquivoOptions = mockupHook.tipoArquivoOptions; // Sempre do mockup

  // Estados de filtro adicionais do mockup (não existem na API)
  const selectedClassificacaoIds = mockupHook.selectedClassificacaoIds;
  const setSelectedClassificacaoIds = mockupHook.setSelectedClassificacaoIds;
  const selectedTipoArquivo = mockupHook.selectedTipoArquivo;
  const setSelectedTipoArquivo = mockupHook.setSelectedTipoArquivo;

  // Classificações para o lightbox — usa API real quando disponível
  const classificacoes = useRealApi ? evidenciasHook.classificacoes : mockupHook.classificacoes;

  // Delete handler
  const handleDelete = useRealApi
    ? async (id: number) => {
        const success = await evidenciasHook.deleteEvidencia(id);
        if (!success) {
          alert('Erro ao deletar evidência');
        }
      }
    : mockupHook.deleteFoto;

  // Update handler - usa API real quando disponível
  const handleUpdate = useRealApi
    ? async (id: number, updates: Partial<MockFoto>) => {
        const apiUpdates: { descricao?: string; classificacao_id?: number } = {};
        if (updates.descricao !== undefined) {
          apiUpdates.descricao = updates.descricao || '';
        }
        if (updates.classificacaoAbrev !== undefined) {
          const classif = classificacoes.find(c => c.abreviacao === updates.classificacaoAbrev);
          if (classif) apiUpdates.classificacao_id = classif.id;
        }
        if (Object.keys(apiUpdates).length > 0) {
          const success = await evidenciasHook.updateEvidencia(id, apiUpdates);
          if (!success) alert('Erro ao atualizar evidência');
        }
      }
    : mockupHook.updateFoto;

  const handleAddFoto = () => {
    alert('Upload de foto (funcionalidade em desenvolvimento)');
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-4" />
        <p className="text-slate-600">Carregando evidências...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md text-center">
          <p className="text-red-800 font-medium mb-2">Erro ao carregar evidências</p>
          <p className="text-red-600 text-sm">{error}</p>
          <button
            onClick={() => evidenciasHook.refresh()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* ======== HEADER FIXO (STICKY) ======== */}
      <div className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900 pb-4 space-y-4">
        {/* Banner de Título - diferente para API vs Mockup */}
        {useRealApi ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Camera className="w-6 h-6 text-blue-600" />
                <div>
                  <h2 className="text-lg font-bold text-blue-800 uppercase tracking-wide">
                    Evidências Fotográficas
                  </h2>
                  <p className="text-xs text-blue-600 mt-0.5">
                    Dados reais da contagem #{jobId}
                  </p>
                </div>
              </div>

              {/* Contadores + Toggle */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5 text-sm">
                  <ImageIcon className="w-4 h-4 text-blue-600" />
                  <span className="font-semibold text-slate-700">{counts.images}</span>
                  <span className="text-slate-500">fotos</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm">
                  <FileText className="w-4 h-4 text-red-500" />
                  <span className="font-semibold text-slate-700">{counts.pdfs}</span>
                  <span className="text-slate-500">docs</span>
                </div>
                {counts.comGps !== undefined && (
                  <div className="flex items-center gap-1.5 text-sm">
                    <MapPin className="w-4 h-4 text-green-600" />
                    <span className="font-semibold text-slate-700">{counts.comGps}</span>
                    <span className="text-slate-500">com GPS</span>
                  </div>
                )}
                {/* View mode toggle */}
                <div className="flex gap-1 bg-blue-100 rounded-lg p-1 ml-2">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                      viewMode === 'grid' ? 'bg-white text-blue-700 shadow-sm' : 'text-blue-600'
                    }`}
                  >
                    Grid
                  </button>
                  <button
                    onClick={() => setViewMode('por_cliente')}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                      viewMode === 'por_cliente' ? 'bg-white text-blue-700 shadow-sm' : 'text-blue-600'
                    }`}
                  >
                    Por Cliente
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Camera className="w-6 h-6 text-amber-600" />
                <div>
                  <h2 className="text-lg font-bold text-amber-800 uppercase tracking-wide">
                    Fotografia (Mockup)
                  </h2>
                  <p className="text-xs text-amber-600 mt-0.5">
                    Dados de demonstração • Mesmos dados para todas as contagens
                  </p>
                </div>
              </div>

              {/* Contadores */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5 text-sm">
                  <ImageIcon className="w-4 h-4 text-blue-600" />
                  <span className="font-semibold text-slate-700">{counts.images}</span>
                  <span className="text-slate-500">fotos</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm">
                  <FileText className="w-4 h-4 text-red-500" />
                  <span className="font-semibold text-slate-700">{counts.pdfs}</span>
                  <span className="text-slate-500">docs</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filtros (horizontal) - Cliente removido, usa filtro global no header */}
        <FotografiasFilters
          // Opções de filtro (clienteOptions removido)
          classificacaoOptions={classificacaoOptions}
          tipoGeralOptions={tipoGeralOptions}
          tipoArquivoOptions={tipoArquivoOptions}
          // Estados de filtro (selectedClienteIds removido)
          selectedTipoGeral={selectedTipoGeral}
          selectedClassificacaoIds={selectedClassificacaoIds}
          selectedTipoArquivo={selectedTipoArquivo}
          searchTerm={searchTerm}
          // Setters (setSelectedClienteIds removido)
          setSelectedTipoGeral={setSelectedTipoGeral}
          setSelectedClassificacaoIds={setSelectedClassificacaoIds}
          setSelectedTipoArquivo={setSelectedTipoArquivo}
          setSearchTerm={setSearchTerm}
          // Ações
          onClearAll={clearAllFilters}
          hasActiveFilters={hasActiveFilters}
        />
      </div>

      {/* ======== ÁREA SCROLLÁVEL DAS FOTOS ======== */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {viewMode === 'grid' ? (
          <FotografiasGrid
            fotos={fotos}
            onPhotoClick={openLightbox}
            onDelete={handleDelete}
            onAdd={handleAddFoto}
          />
        ) : (
          <FotografiasPorClienteView
            fotos={fotos}
            jobClientes={jobClientes}
            onPhotoClick={openLightbox}
            onDelete={handleDelete}
          />
        )}
      </div>

      {/* Lightbox (Modal) */}
      <FotoLightbox
        isOpen={lightboxOpen}
        fotos={fotos}
        currentIndex={currentPhotoIndex}
        classificacoes={classificacoes}
        onClose={closeLightbox}
        onPrevious={goToPrevious}
        onNext={goToNext}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
      />
    </div>
  );
}

export default FotografiasTab;
